import React, { useEffect, useState } from 'react';
import { permissionsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { 
    Shield,
    ShieldCheck,
    ShieldAlert,
    Eye,
    Save,
    RotateCcw,
    Check,
    X,
    Settings,
    ShoppingCart,
    Package,
    ClipboardList,
    Factory,
    Building2,
    MapPin,
    Users,
    RefreshCw,
    BarChart3,
    TrendingUp,
    ChefHat,
    Truck,
    Wallet,
    Trash2,
    LayoutDashboard,
    Percent,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLES = [
    { value: 'admin', label: 'Administrateur', description: 'Accès complet à toutes les fonctionnalités', icon: ShieldCheck, color: 'bg-purple-100 text-purple-700 border-purple-200' },
    { value: 'supervisor', label: 'Superviseur', description: 'Gestion étendue sans administration des utilisateurs', icon: ShieldCheck, color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    { value: 'manager', label: 'Manager', description: 'Gestion des commandes et inventaires', icon: Shield, color: 'bg-blue-100 text-blue-700 border-blue-200' },
    { value: 'chef_cuisine', label: 'Chef de Cuisine', description: 'Recettes, produits et pertes — droits à la carte', icon: Shield, color: 'bg-orange-100 text-orange-700 border-orange-200' },
    { value: 'livreur', label: 'Livreur', description: 'Réceptions et suivi des livraisons', icon: Truck, color: 'bg-amber-100 text-amber-700 border-amber-200' },
    { value: 'operator', label: 'Opérateur', description: 'Saisie des réceptions et inventaires', icon: ShieldAlert, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    { value: 'facturation', label: 'Facturation', description: 'Lecture seule + Production Concept Lab en écriture', icon: Wallet, color: 'bg-teal-100 text-teal-700 border-teal-200' },
    { value: 'production', label: 'Production', description: 'Page Production + impression des étiquettes', icon: Factory, color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    { value: 'comptabilite', label: 'Comptabilité', description: 'Consultation financière — droits à affiner', icon: Wallet, color: 'bg-rose-100 text-rose-700 border-rose-200' },
    { value: 'readonly', label: 'Lecture seule', description: 'Consultation uniquement', icon: Eye, color: 'bg-slate-100 text-slate-700 border-slate-200' },
];

// Groupement des permissions par catégorie
const PERMISSION_GROUPS = [
    {
        id: 'general',
        label: 'Général',
        icon: LayoutDashboard,
        color: 'text-slate-600',
        permissions: ['dashboard_view', 'guide_view']
    },
    {
        id: 'orders',
        label: 'Commandes',
        icon: ShoppingCart,
        color: 'text-blue-600',
        permissions: [
            'orders_view',
            'consolidation_view',
            'refacturation_view',
            'orders_create',
            'orders_edit',
            'orders_delete',
            'orders_send_email',
            'orders_send_whatsapp',
            'orders_export_excel',
            'orders_import_invoice',
        ]
    },
    {
        id: 'receptions',
        label: 'Réceptions',
        icon: Package,
        color: 'text-emerald-600',
        permissions: ['receptions_view', 'receptions_create']
    },
    {
        id: 'transfers',
        label: 'Transferts',
        icon: Truck,
        color: 'text-orange-600',
        permissions: ['transfers_view', 'transfers_create', 'transfers_validate']
    },
    {
        id: 'inventories',
        label: 'Inventaires',
        icon: ClipboardList,
        color: 'text-amber-600',
        permissions: ['stock_page_view', 'inventories_view', 'inventories_create', 'inventories_edit', 'inventories_delete', 'inventory_forms_manage']
    },
    {
        id: 'products',
        label: 'Produits industriels',
        icon: Factory,
        color: 'text-violet-600',
        permissions: ['products_view', 'products_create', 'products_edit', 'products_delete']
    },
    {
        id: 'supplier_products',
        label: 'Produits fournisseurs',
        icon: Package,
        color: 'text-cyan-600',
        permissions: ['supplier_products_view', 'supplier_products_create', 'supplier_products_edit', 'supplier_products_delete', 'supplier_products_commandable']
    },
    {
        id: 'suppliers',
        label: 'Fournisseurs',
        icon: Building2,
        color: 'text-orange-600',
        permissions: ['suppliers_view', 'suppliers_edit']
    },
    {
        id: 'recipes',
        label: 'Recettes',
        icon: ChefHat,
        color: 'text-amber-700',
        permissions: ['recipes_view', 'recipes_create', 'recipes_edit', 'recipes_delete', 'prices_view']
    },
    {
        id: 'sites',
        label: 'Sites',
        icon: MapPin,
        color: 'text-rose-600',
        permissions: ['sites_view', 'sites_edit']
    },
    {
        id: 'users',
        label: 'Utilisateurs',
        icon: Users,
        color: 'text-indigo-600',
        permissions: ['users_view', 'users_edit', 'users_logins_view']
    },
    {
        id: 'sales',
        label: 'Ventes (par onglet)',
        icon: Wallet,
        color: 'text-pink-600',
        permissions: [
            'sales_financial_view',
            'sales_entries_view',
            'sales_entries_edit',
            'sales_evolution_view',
            'sales_top_view',
            'sales_pivot_view',
            'sales_vitrine_view',
            'sales_payroll_view',
            'sales_import',
            'sales_myfood_view',
            'sales_myfood_financial_view',
        ]
    },
    {
        id: 'ratios',
        label: 'Ratios',
        icon: Percent,
        color: 'text-indigo-600',
        permissions: ['ratios_view']
    },
    {
        id: 'losses',
        label: 'Pertes',
        icon: Trash2,
        color: 'text-yellow-600',
        permissions: ['losses_entry', 'losses_cl_entry']
    },
    {
        id: 'finance',
        label: 'Suivi financier',
        icon: Wallet,
        color: 'text-emerald-700',
        permissions: ['finance_view', 'finance_edit']
    },
    {
        id: 'production',
        label: 'Production Concept Lab',
        icon: Factory,
        color: 'text-teal-600',
        permissions: ['production_view', 'production_edit', 'production_billing_view', 'labels_view', 'labels_print', 'recipes_view']
    },
    {
        id: 'other',
        label: 'Autres',
        icon: Settings,
        color: 'text-slate-600',
        permissions: ['permissions_manage', 'sync_access', 'stats_view', 'consumption_view']
    }
];

export default function RolePermissionsPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [availablePermissions, setAvailablePermissions] = useState([]);
    const [rolePermissions, setRolePermissions] = useState({});
    const [originalPermissions, setOriginalPermissions] = useState({});
    const [selectedRole, setSelectedRole] = useState('manager');
    const [hasChanges, setHasChanges] = useState(false);
    const [resetDialog, setResetDialog] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        // Vérifier si les permissions ont changé
        const original = JSON.stringify(originalPermissions[selectedRole] || []);
        const current = JSON.stringify(rolePermissions[selectedRole] || []);
        setHasChanges(original !== current);
    }, [rolePermissions, originalPermissions, selectedRole]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [availableRes, rolesRes] = await Promise.all([
                permissionsAPI.getAvailable(),
                permissionsAPI.getAllRoles()
            ]);
            setAvailablePermissions(availableRes.data.permissions);
            setRolePermissions(rolesRes.data);
            setOriginalPermissions(JSON.parse(JSON.stringify(rolesRes.data)));
        } catch (error) {
            toast.error('Erreur lors du chargement des permissions');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getPermissionLabel = (key) => {
        const perm = availablePermissions.find(p => p.key === key);
        return perm?.label || key;
    };

    const togglePermission = (permKey) => {
        if (selectedRole === 'admin') {
            toast.error('Les permissions administrateur ne peuvent pas être modifiées');
            return;
        }
        
        setRolePermissions(prev => {
            const currentPerms = prev[selectedRole] || [];
            const newPerms = currentPerms.includes(permKey)
                ? currentPerms.filter(p => p !== permKey)
                : [...currentPerms, permKey];
            return { ...prev, [selectedRole]: newPerms };
        });
    };

    const toggleGroupPermissions = (groupPerms) => {
        if (selectedRole === 'admin') {
            toast.error('Les permissions administrateur ne peuvent pas être modifiées');
            return;
        }
        
        const currentPerms = rolePermissions[selectedRole] || [];
        const allEnabled = groupPerms.every(p => currentPerms.includes(p));
        
        setRolePermissions(prev => {
            let newPerms;
            if (allEnabled) {
                // Désactiver toutes les permissions du groupe
                newPerms = currentPerms.filter(p => !groupPerms.includes(p));
            } else {
                // Activer toutes les permissions du groupe
                newPerms = [...new Set([...currentPerms, ...groupPerms])];
            }
            return { ...prev, [selectedRole]: newPerms };
        });
    };

    const savePermissions = async () => {
        try {
            setSaving(true);
            await permissionsAPI.updateRolePermissions(selectedRole, rolePermissions[selectedRole]);
            setOriginalPermissions(prev => ({ ...prev, [selectedRole]: [...rolePermissions[selectedRole]] }));
            toast.success('Permissions enregistrées');
            setHasChanges(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    const resetPermissions = async () => {
        try {
            setSaving(true);
            const response = await permissionsAPI.resetRolePermissions(selectedRole);
            setRolePermissions(prev => ({ ...prev, [selectedRole]: response.data.permissions }));
            setOriginalPermissions(prev => ({ ...prev, [selectedRole]: response.data.permissions }));
            toast.success('Permissions réinitialisées');
            setResetDialog(false);
            setHasChanges(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
        } finally {
            setSaving(false);
        }
    };

    const cancelChanges = () => {
        setRolePermissions(prev => ({ 
            ...prev, 
            [selectedRole]: [...(originalPermissions[selectedRole] || [])] 
        }));
        setHasChanges(false);
    };

    const currentPerms = rolePermissions[selectedRole] || [];
    const roleInfo = ROLES.find(r => r.value === selectedRole);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="role-permissions-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Permissions des rôles</h1>
                    <p className="text-slate-600 mt-1">Configurez les accès pour chaque profil utilisateur</p>
                </div>
            </div>

            {/* Role Tabs */}
            <Tabs value={selectedRole} onValueChange={setSelectedRole}>
                <TabsList className="grid w-full grid-cols-5 h-auto p-1">
                    {ROLES.map(role => {
                        const Icon = role.icon;
                        return (
                            <TabsTrigger 
                                key={role.value} 
                                value={role.value}
                                className="flex items-center gap-2 py-3 data-[state=active]:bg-white"
                                data-testid={`tab-${role.value}`}
                            >
                                <Icon className="w-4 h-4" />
                                <span className="hidden sm:inline">{role.label}</span>
                            </TabsTrigger>
                        );
                    })}
                </TabsList>

                {ROLES.map(role => (
                    <TabsContent key={role.value} value={role.value} className="mt-6">
                        {/* Role Info Card */}
                        <Card className={`mb-6 border-2 ${role.color}`}>
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-lg ${role.color}`}>
                                            <role.icon className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <CardTitle>{role.label}</CardTitle>
                                            <CardDescription>{role.description}</CardDescription>
                                        </div>
                                    </div>
                                    <Badge variant="outline" className="text-sm">
                                        {currentPerms.length} / {availablePermissions.length} permissions
                                    </Badge>
                                </div>
                            </CardHeader>
                        </Card>

                        {role.value === 'admin' && (
                            <Card className="mb-6 border-purple-200 bg-purple-50/50">
                                <CardContent className="py-4">
                                    <p className="text-sm text-purple-700 flex items-center gap-2">
                                        <ShieldCheck className="w-4 h-4" />
                                        Les administrateurs ont accès à toutes les fonctionnalités. Ces permissions ne peuvent pas être modifiées.
                                    </p>
                                </CardContent>
                            </Card>
                        )}

                        {/* Permission Groups */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {PERMISSION_GROUPS.map(group => {
                                const GroupIcon = group.icon;
                                const groupPerms = group.permissions;
                                const enabledCount = groupPerms.filter(p => currentPerms.includes(p)).length;
                                const allEnabled = enabledCount === groupPerms.length;
                                const someEnabled = enabledCount > 0 && enabledCount < groupPerms.length;
                                
                                return (
                                    <Card key={group.id} className="overflow-hidden">
                                        <CardHeader className="pb-2 bg-slate-50 border-b">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <GroupIcon className={`w-5 h-5 ${group.color}`} />
                                                    <CardTitle className="text-base">{group.label}</CardTitle>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Badge variant="outline" className="text-xs">
                                                        {enabledCount}/{groupPerms.length}
                                                    </Badge>
                                                    {role.value !== 'admin' && (
                                                        <Switch
                                                            checked={allEnabled}
                                                            onCheckedChange={() => toggleGroupPermissions(groupPerms)}
                                                            className={someEnabled ? 'data-[state=unchecked]:bg-amber-400' : ''}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="pt-3">
                                            <div className="space-y-2">
                                                {groupPerms.map(permKey => {
                                                    const enabled = currentPerms.includes(permKey);
                                                    return (
                                                        <div 
                                                            key={permKey}
                                                            className={`flex items-center justify-between p-2 rounded-lg transition-colors ${
                                                                enabled ? 'bg-emerald-50' : 'bg-slate-50'
                                                            } ${role.value !== 'admin' ? 'cursor-pointer hover:bg-slate-100' : ''}`}
                                                            onClick={() => role.value !== 'admin' && togglePermission(permKey)}
                                                        >
                                                            <span className={`text-sm ${enabled ? 'text-slate-900' : 'text-slate-500'}`}>
                                                                {getPermissionLabel(permKey)}
                                                            </span>
                                                            {enabled ? (
                                                                <Check className="w-4 h-4 text-emerald-600" />
                                                            ) : (
                                                                <X className="w-4 h-4 text-slate-300" />
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>

                        {/* Action Buttons */}
                        {role.value !== 'admin' && (
                            <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t">
                                <Button
                                    variant="outline"
                                    onClick={() => setResetDialog(true)}
                                    disabled={saving}
                                >
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                    Réinitialiser
                                </Button>
                                {hasChanges && (
                                    <Button
                                        variant="outline"
                                        onClick={cancelChanges}
                                        disabled={saving}
                                    >
                                        Annuler
                                    </Button>
                                )}
                                <Button
                                    onClick={savePermissions}
                                    disabled={saving || !hasChanges}
                                    className="bg-slate-900 hover:bg-slate-800"
                                    data-testid="save-permissions-btn"
                                >
                                    {saving ? (
                                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Save className="w-4 h-4 mr-2" />
                                    )}
                                    Enregistrer
                                </Button>
                            </div>
                        )}
                    </TabsContent>
                ))}
            </Tabs>

            {/* Reset Confirmation Dialog */}
            <AlertDialog open={resetDialog} onOpenChange={setResetDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Réinitialiser les permissions ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Les permissions du profil <strong>{roleInfo?.label}</strong> seront réinitialisées aux valeurs par défaut.
                            Cette action ne peut pas être annulée.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={resetPermissions}>
                            Réinitialiser
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
